import React, { useEffect, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import useCachedSubscription from '../hooks/useCachedSubscription';
import { useNavigate } from 'react-router-dom';

// === Config (env-first, mirroring SubscriptionTestPage.jsx) ===
const API_BASE =
  process.env.REACT_APP_API_BASE ||
  'https://bp7u0bhrc8.execute-api.eu-west-2.amazonaws.com/dev';

const PRICE_ID =
  process.env.REACT_APP_STRIPE_PRICE_ID ||
  'price_1S2GNkAOzO0zxQTK1EWByx6L';

// Small fetch helper (JSON in/out + error surfacing) — same as SubscriptionTestPage.jsx
async function http(url, init = {}) {
  const res = await fetch(url, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init.headers || {}) },
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok || data.error) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

export default function RealBilling() {
  // Mirror SubscriptionTestPage.jsx state/flow
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [cachedIdToken, setCachedIdToken] = useState(null);
  const [precreatedCheckoutUrl, setPrecreatedCheckoutUrl] = useState(null);

  const { subscription, loading: subLoading, refresh } = useCachedSubscription();
  const isActive = !subLoading && subscription?.status === 'active';

  // NEW: setup loader after returning from Stripe
  const [settingUp, setSettingUp] = useState(false);

  const navigate = useNavigate();

  // Immediately check subscription on page load
  useEffect(() => {
    refresh();
  }, [refresh]);

  // NEW: if user returned from login with an intent to subscribe, auto-start the checkout
  useEffect(() => {
    const intent = localStorage.getItem('medmap.intent.subscribe');
    if (!intent) return;

    let cancelled = false;
    (async () => {
      try {
        const { tokens } = await fetchAuthSession();
        const idToken = tokens?.idToken?.toString();
        if (!idToken) return; // still not signed in; do nothing
        // Clear the intent and kick off checkout
        localStorage.removeItem('medmap.intent.subscribe');
        if (!cancelled) startSubscription();
      } catch {
        // ignore; user not authenticated
      }
    })();

    return () => { cancelled = true; };
  }, []); // run once on mount

  // NEW: handle session_id redirects from Stripe and show 1s loader
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) return;

    let cancelled = false;
    setSettingUp(true);

    (async () => {
      try {
        // Fetch the completed Checkout Session details
        const data = await http(
          `${API_BASE}/checkout/session?id=${encodeURIComponent(sessionId)}`
        );

        // Persist identifiers for reuse
        const customerId =
          data.customerId ?? data.customer ?? data.customer_id ?? null;
        const subscriptionId =
          data.subscriptionId ?? data.subscription ?? data.subscription_id ?? null;
        const userId =
          data.userId ?? data.clientReferenceId ?? data.client_reference_id ?? null;

        if (customerId)      localStorage.setItem('stripeCustomerId', customerId);
        if (subscriptionId)  localStorage.setItem('stripeSubscriptionId', subscriptionId);
        if (userId)          localStorage.setItem('cognitoUserId', userId);

        // Refresh cached subscription status
        await refresh();
      } catch (e) {
        console.debug('Post-checkout session fetch failed:', e?.message || e);
      } finally {
        try {
          const clean = `${window.location.origin}${window.location.pathname}`;
          window.history.replaceState({}, '', clean);
        } catch {/* noop */}
        setTimeout(() => { if (!cancelled) setSettingUp(false); }, 1000);
      }
    })();

    return () => { cancelled = true; };
  }, [refresh]);

  // Precreate a checkout session once we have an auth token (skip if already active or setting up)
  useEffect(() => {
    let mounted = true;
    if (isActive || settingUp) return; // don't precreate if subscribed or during setup

    const precreate = async () => {
      try {
        const { tokens } = await fetchAuthSession();
        const idToken = tokens?.idToken?.toString();
        if (!idToken) return;
        setCachedIdToken(idToken);

        // keep sending known customerId; server now validates it safely
        const knownCustomer = localStorage.getItem('stripeCustomerId') || null;

        const body = {
          priceId: PRICE_ID,
          successUrl: `${window.location.origin}/billing?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/billing`,
          ...(knownCustomer ? { customerId: knownCustomer } : {}),
        };

        const { url } = await http(`${API_BASE}/create-checkout-session`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${idToken}` },
          body: JSON.stringify(body),
        });

        if (mounted && url) setPrecreatedCheckoutUrl(url);
      } catch (err) {
        console.debug('Precreate checkout failed:', err.message);
      }
    };

    precreate();
    return () => { mounted = false; };
  }, [isActive, settingUp]);

  // Start subscription — identical control flow to SubscriptionTestPage.jsx
  const startSubscription = async () => {
    try {
      setBusy(true);
      setNote('Creating checkout session…');

      if (precreatedCheckoutUrl) {
        window.location.assign(precreatedCheckoutUrl);
        return;
      }

      const idToken =
        cachedIdToken || (await fetchAuthSession()).tokens?.idToken?.toString();
      if (!idToken) throw new Error('Not signed in');

      const knownCustomer = localStorage.getItem('stripeCustomerId') || null;

      const { url, portalUrl, customerId: cidFromCreate } = await http(
        `${API_BASE}/create-checkout-session`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({
            priceId: PRICE_ID,
            successUrl: `${window.location.origin}/billing?session_id={CHECKOUT_SESSION_ID}`,
            cancelUrl: `${window.location.origin}/billing`,
            ...(knownCustomer ? { customerId: knownCustomer } : {}),
          }),
        }
      );

      if (cidFromCreate && /^cus_/.test(cidFromCreate)) {
        localStorage.setItem('stripeCustomerId', cidFromCreate);
      }

      const redirectUrl = url || portalUrl;
      if (!redirectUrl) throw new Error('No redirect URL from backend');
      window.location.assign(redirectUrl);
    } catch (e) {
      setNote(`❌ ${e.message}`);
      setBusy(false);
    }
  };

  // Edit: on CTA click, if logged out → set intent + redirect to login; else proceed to checkout
  const handleCtaClick = async (e) => {
    const btn = e.currentTarget;
    btn.classList.remove('clicked');
    void btn.offsetWidth;
    btn.classList.add('clicked');

    try {
      const { tokens } = await fetchAuthSession();
      const idToken = tokens?.idToken?.toString();
      if (!idToken) {
        try { localStorage.setItem('medmap.intent.subscribe', '1'); } catch {}
        navigate('/login', { state: { from: '/billing' } });
        return;
      }
    } catch {
      try { localStorage.setItem('medmap.intent.subscribe', '1'); } catch {}
      navigate('/login', { state: { from: '/billing' } });
      return;
    }

    // If authenticated, start checkout flow
    startSubscription();

    setTimeout(() => btn.classList.remove('clicked'), 600);
  };

  // Loader
  if (settingUp) {
    return (
      <main className="rbilling-setup" role="status" aria-live="polite">
        <div className="rbilling-setup-card">
          <h2>Setting up your account…</h2>
          <div className="rbilling-setup-bar">
            <div className="rbilling-setup-fill" />
          </div>
          <p className="rbilling-setup-sub">This will only take a moment.</p>
        </div>

        <style>{`
          .rbilling-setup {
            padding: 80px 16px 64px;
            display: grid;
            place-items: center;
            min-height: 100vh;
            background: linear-gradient(180deg, #eef2ff, #ffffff);
          }
          .rbilling-setup-card {
            width: min(560px, 92vw);
            background: #fff;
            border: 1px solid rgba(79,124,255,0.12);
            border-radius: 14px;
            padding: 24px 20px;
            box-shadow: 0 6px 20px rgba(0,0,0,0.06);
            text-align: center;
          }
          .rbilling-setup-card h2 {
            margin: 0 0 14px;
            font-size: 1.25rem;
            font-weight: 700;
            color: #111827;
          }
          .rbilling-setup-bar {
            height: 8px;
            background: #e5e7eb;
            border-radius: 6px;
            overflow: hidden;
            position: relative;
          }
          .rbilling-setup-fill {
            height: 100%;
            width: 60%;
            background: linear-gradient(90deg, #4f7cff 0%, #8aa2ff 100%);
            border-radius: 6px;
            animation: rbilling-setup-anim 1s ease-in-out infinite;
          }
          @keyframes rbilling-setup-anim {
            0%   { transform: translateX(-60%); }
            50%  { transform: translateX(0%); }
            100% { transform: translateX(60%); }
          }
          .rbilling-setup-sub {
            margin: 10px 0 0;
            color: #6b7280;
            font-size: 0.9rem;
          }
          /* @media (prefers-color-scheme: dark) {
            .rbilling-setup {
              background: linear-gradient(180deg, #0b1220, #0f172a);
            }
            .rbilling-setup-card {
              background: #0f172a;
              border-color: rgba(122,162,255,0.22);
              box-shadow: none;
            }
            .rbilling-setup-card h2 { color: #e5e7eb; }
            .rbilling-setup-sub { color: #94a3b8; }
            .rbilling-setup-bar { background: #1f2937; }
          } */
        `}</style>
      </main>
    );
  }

  return (
    <main className="rbilling-page" role="main">
      <section className="rbilling-hero">
        <div className="rbilling-hero-inner">
          {/* Subscribed banner */}
          {isActive && (
            <div className="rbilling-subscribed-banner" role="status" aria-live="polite">
              <span className="rbilling-subscribed-dot" aria-hidden="true" /> Subscribed!
            </div>
          )}
          <div className="rbilling-badge">75% off</div>
          <h1 className="rbilling-title">MedMap Pro</h1>
          <p className="rbilling-subtitle">Unlock premium pathways and tracking for your training journey.</p>

          <div className="rbilling-pricing">
            <div className="rbilling-price-now">
              <span className="rbilling-currency">£</span>
              <span className="rbilling-amount">2.99</span>
              <span className="rbilling-per">/mo</span>
            </div>
            <div className="rbilling-price-was">
              <span className="rbilling-strike">£11.99</span>
            </div>
          </div>

          <div className="rbilling-cta">
            <button
              className="rbilling-btn primary"
              type="button"
              aria-label={isActive ? 'Manage billing' : 'Subscribe now'}
              onClick={handleCtaClick}
            >
              <span className="rbilling-btn-icon" aria-hidden="true">
                {/* arrow icon */}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              {isActive ? 'Manage Billing' : 'Subscribe now'}
            </button>

            {isActive && (
              <a
                href="/pathfinder-setup"
                className="rbilling-btn ghost"
                aria-label="Unlock Pathfinder"
              >
                Unlock Pathfinder
              </a>
            )}
          </div>
        </div>
      </section>

      <section className="rbilling-section">
        <div className="rbilling-card">
          <h2 className="rbilling-section-title">What you get</h2>
          <ul className="rbilling-features">
            <li className="rbilling-feature">
              <span className="rbilling-check" aria-hidden="true">
                {/* check icon */}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M20 7L9 18l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              Pathfinder Tracking
            </li>
            <li className="rbilling-feature">
              <span className="rbilling-check" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M20 7L9 18l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              IMT/CST/ACCS training pathways
            </li>
            <li className="rbilling-feature">
              <span className="rbilling-check" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M20 7L9 18l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              Detailed Substeps for all domains
            </li>
            <li className="rbilling-feature">
              <span className="rbilling-check" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M20 7L9 18l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              And more…
            </li>
          </ul>

          <div className="rbilling-hint">
            No commitments. Cancel anytime from your billing settings.
          </div>

          <div className="rbilling-cta secondary">
            <button
              className="rbilling-btn outline"
              type="button"
              aria-label={isActive ? 'Manage billing' : 'Start for £2.99'}
              onClick={handleCtaClick}
            >
              {isActive ? 'Manage Billing' : 'Start for £2.99'}
            </button>

            {isActive && (
              <a
                href="/pathfinder-setup"
                className="rbilling-btn subtle"
                aria-label="Unlock Pathfinder"
              >
                Unlock Pathfinder
              </a>
            )}
          </div>

          {note && <p className="rbilling-note" style={{ marginTop: 12 }}>{note}</p>}
        </div>
      </section>

      <style>{`
        .rbilling-page {
          --rb-bg: #0b1220;
          --rb-fg: #0f172a;
          --rb-card: #ffffff;
          --rb-muted: #6b7280;
          --rb-accent: #4f7cff;
          --rb-accent-2: #8aa2ff;
          --rb-success: #10b981;
          --rb-veil: 222, 231, 255;
          color: #111827;
          padding: 80px 16px 64px;
        }
        /* @media (prefers-color-scheme: dark) {
          .rbilling-page {
            --rb-bg: #0b1220;
            --rb-fg: #e5e7eb;
            --rb-card: #0f172a;
            --rb-muted: #94a3b8;
            --rb-accent: #7aa2ff;
            --rb-accent-2: #a8baff;
            --rb-success: #34d399;
            color: var(--rb-fg);
          }
        } */

        .rbilling-hero {
          position: relative;
          border-radius: 14px;
          background:
            radial-gradient(1200px 300px at 50% -20%, rgba(var(--rb-veil), .55), transparent),
            linear-gradient(180deg, #eef2ff, #ffffff);
          padding: 32px;
          box-shadow: 0 8px 30px rgba(0,0,0,0.08);
          border: 1px solid rgba(79, 124, 255, 0.12);
        }
        /* @media (prefers-color-scheme: dark) {
          .rbilling-hero {
            background:
              radial-gradient(1200px 300px at 50% -20%, rgba(79,124,255,.15), transparent),
              linear-gradient(180deg, #0b1220, #0f172a);
            border-color: rgba(122,162,255,0.22);
            box-shadow: inset 0 1px 0 rgba(255,255,255,0.02);
          }
        } */
        .rbilling-hero-inner {
          max-width: 960px;
          margin: 0 auto;
          text-align: center;
        }

        .rbilling-badge {
          display: inline-block;
          padding: 6px 10px;
          font-weight: 700;
          font-size: 12px;
          color: #0b1220;
          background: linear-gradient(90deg, #fce7f3, #dbeafe);
          border: 1px solid rgba(0,0,0,0.06);
          border-radius: 999px;
        }
        /* @media (prefers-color-scheme: dark) {
          .rbilling-badge { color: #0f172a; border-color: rgba(255,255,255,0.06); }
        } */

        .rbilling-title {
          margin: 12px 0 6px;
          font-size: clamp(28px, 4vw, 40px);
          letter-spacing: -0.02em;
        }
        .rbilling-subtitle {
          margin: 0 auto 18px;
          max-width: 680px;
          color: var(--rb-muted);
          font-size: 16px;
        }

        .rbilling-pricing {
          display: flex;
          align-items: baseline;
          justify-content: center;
          gap: 16px;
          margin: 10px 0 14px;
        }
        .rbilling-price-now {
          display: inline-flex;
          align-items: baseline;
          gap: 4px;
          color: var(--rb-accent);
        }
        .rbilling-currency { font-size: 20px; font-weight: 700; }
        .rbilling-amount   { font-size: clamp(32px, 6vw, 48px); font-weight: 800; }
        .rbilling-per      { font-size: 14px; color: var(--rb-muted); margin-left: 2px; }
        .rbilling-price-was .rbilling-strike {
          color: var(--rb-muted);
          text-decoration: line-through;
          text-decoration-thickness: 2px;
        }

        .rbilling-cta {
          display: flex;
          gap: 12px;
          justify-content: center;
          flex-wrap: wrap;
          margin-top: 8px;
        }

        .rbilling-section {
          max-width: 960px;
          margin: 24px auto 0;
        }

        .rbilling-card {
          background: var(--rb-card);
          border: 1px solid rgba(79, 124, 255, 0.12);
          border-radius: 14px;
          padding: 18px;
          box-shadow: 0 6px 20px rgba(0,0,0,0.06);
        }
        /* @media (prefers-color-scheme: dark) {
          .rbilling-card {
            border-color: rgba(122,162,255,0.22);
            box-shadow: none;
          }
        } */

        .rbilling-section-title {
          margin: 4px 4px 10px;
          font-size: 18px;
        }

        .rbilling-features {
          list-style: none;
          padding: 0;
          margin: 0;
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px 12px;
        }
        @media (max-width: 640px) {
          .rbilling-features { grid-template-columns: 1fr; }
        }
        .rbilling-feature {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          border-radius: 12px;
          background: linear-gradient(180deg, rgba(79,124,255,0.05), rgba(79,124,255,0.02));
          border: 1px solid rgba(79, 124, 255, 0.12);
        }
        /* @media (prefers-color-scheme: dark) {
          .rbilling-feature {
            background: linear-gradient(180deg, rgba(122,162,255,0.08), rgba(122,162,255,0.03));
            border-color: rgba(122,162,255,0.22);
          }
        } */
        .rbilling-check {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: var(--rb-success);
          background: rgba(16,185,129,0.08);
          border: 1px solid rgba(16,185,129,0.22);
          border-radius: 999px;
          width: 26px;
          height: 26px;
          flex: 0 0 26px;
        }

        .rbilling-hint {
          margin-top: 14px;
          color: var(--rb-muted);
          font-size: 13px;
          text-align: center;
        }

        .rbilling-cta.secondary {
          justify-content: center;
          margin-top: 14px;
          gap: 10px;
        }

        .rbilling-btn {
          appearance: none;
          border: 1px solid transparent;
          border-radius: 10px;
          min-height: 42px;
          padding: 10px 14px;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          cursor: default; /* non-functional for now */
          transition: filter 0.15s ease, transform 0.02s ease;
          position: relative;           /* enable ripple positioning */
          overflow: hidden;             /* mask ripple */
        }
        .rbilling-btn .rbilling-btn-icon { display: inline-flex; }
        .rbilling-btn.primary {
          background: linear-gradient(180deg, var(--rb-accent), var(--rb-accent-2));
          color: white;
          border-color: rgba(0,0,0,0.06);
        }
        .rbilling-btn.ghost {
          background: #f3f4f6;
          color: #111827;
          border-color: #e5e7eb;
        }
        .rbilling-btn.outline {
          background: transparent;
          color: var(--rb-accent);
          border-color: currentColor;
        }
        .rbilling-btn.subtle {
          background: transparent;
          color: var(--rb-muted);
          border-color: rgba(0,0,0,0.08);
        }
        /* @media (prefers-color-scheme: dark) {
          .rbilling-btn.ghost {
            background: #1f2937;
            color: #e5e7eb;
            border-color: #374151;
          }
          .rbilling-btn.subtle { border-color: rgba(255,255,255,0.12); }
        } */

        /* Click pulse + ripple */
        .rbilling-btn.clicked {
          animation: rbilling-press 220ms cubic-bezier(.34,1.56,.64,1);
        }
        .rbilling-btn::after {
          content: '';
          position: absolute;
          left: 50%;
          top: 50%;
          width: 10px;
          height: 10px;
          border-radius: 999px;
          background: rgba(255,255,255,0.35);
          transform: translate(-50%,-50%) scale(1);
          opacity: 0;
          pointer-events: none;
        }
        .rbilling-btn.outline::after {
          background: rgba(79,124,255,0.25);
        }
        .rbilling-btn.clicked::after {
          animation: rbilling-ripple 600ms ease-out forwards;
        }
        @keyframes rbilling-press {
          0%   { transform: translateY(0) scale(1); }
          50%  { transform: translateY(0) scale(0.98); }
          100% { transform: translateY(0) scale(1); }
        }
        @keyframes rbilling-ripple {
          0%   { opacity: .35; transform: translate(-50%,-50%) scale(1); }
          100% { opacity: 0;   transform: translate(-50%,-50%) scale(35); }
        }

        /* Responsive paddings */
        @media (max-width: 640px) {
          .rbilling-hero { padding: 22px; }
          .rbilling-card { padding: 14px; }
        }

        /* Subscribed banner styles */
        .rbilling-subscribed-banner {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          border-radius: 999px;
          background: #ecfdf5;
          color: #065f46;
          border: 1px solid #a7f3d0;
          font-weight: 700;
          font-size: 14px;
          margin-bottom: 8px;
          box-shadow: 0 6px 16px rgba(16,185,129,0.18);
        }
        .rbilling-subscribed-dot {
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: #10b981;
          box-shadow: 0 0 0 4px rgba(16,185,129,0.15);
        }
      `}</style>
    </main>
  );
}